import type { IOnChangeFunnelParams } from '~/presentation/charts/components/GFunnelChart/components/types';
import type { IBasePathAnalyticPageWithChild, IBasePathAnalyticPageWithParent } from '../types';
import type { IPathAnalysisFirstLevel } from '../contexts';

import { processPathAnalytics, processPathAnalyticsTree } from '../helpers/pathAnalysis';
import { useFunnelChartStore, usePathAnalysisStore } from '../stores';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LOCATION_PATH_KEY } from '~/modules/page-analytic';
import { MAX_FUNNEL_ADDITIONAL_LEVEL } from '../configs';
import { getFunnelChildrenPages } from '../helpers';
import { useSearchParams } from '@remix-run/react';

interface IUsePathAnalysisFunnelProps {
  pathAnalytics: IBasePathAnalyticPageWithParent[];
  pathAnalysisCacheKey: string;
  dataFirstLevels?: IPathAnalysisFirstLevel[];
}

export const usePathAnalysisFunnel = ({
  pathAnalytics,
  pathAnalysisCacheKey,
  dataFirstLevels,
}: IUsePathAnalysisFunnelProps) => {
  const [searchParams] = useSearchParams();

  const setFirstLevelLocationPath = usePathAnalysisStore(
    (state) => state.setFirstLevelLocationPath,
  );
  const firstLevelLocationPath =
    usePathAnalysisStore((state) => state.firstLevelLocationPath) ||
    searchParams.get(LOCATION_PATH_KEY);

  const setCachedFunnelChartData = useFunnelChartStore((state) => state.setCachedFunnelChartData);
  const resetCachedFunnelChartData = useFunnelChartStore((state) => state.resetData);
  const cachedFunnelChartData = useFunnelChartStore((state) => state.cachedFunnelChartData);

  const [firstLevelPage, setFirstLevelPage] = useState<string | undefined>(undefined);
  const [dynamicLevels, setDynamicLevels] = useState<IBasePathAnalyticPageWithChild[]>([]);

  const dataTree = useMemo(() => processPathAnalyticsTree(pathAnalytics), [pathAnalytics]);

  const listPageAnalyticFirstLevel = useMemo(() => {
    return dataTree?.filter((item) => item.level === 1 && item.id) || [];
  }, [dataTree]);

  //pageWithMetric: Build path tree by level 1
  const pageWithMetric = useMemo(
    () => processPathAnalytics(pathAnalytics, firstLevelPage || ''),
    [pathAnalytics, firstLevelPage],
  );

  const nextLevelPages = useMemo(() => {
    const lastLevel = dynamicLevels[dynamicLevels.length - 1] || pageWithMetric;
    const nextLevelPages = getFunnelChildrenPages(lastLevel);
    return nextLevelPages;
  }, [dynamicLevels, pageWithMetric]);

  const handleSetFirstLevelPage = (path: string) => {
    setFirstLevelPage(path);

    const cachedData = cachedFunnelChartData[pathAnalysisCacheKey];
    setDynamicLevels(cachedData?.dynamicLevels || []);
  };

  const handleAddNextLevel = useCallback(
    (childId: string) => {
      if (dynamicLevels.length >= MAX_FUNNEL_ADDITIONAL_LEVEL) {
        return;
      }
      const selectedPage = nextLevelPages.find((page) => page.id === childId);
      if (selectedPage) {
        setDynamicLevels((prev) => [...prev, selectedPage]);
      }
    },
    [dynamicLevels.length, nextLevelPages],
  );

  const getPagesByParentID = (data: { level?: number; parentID?: string }) => {
    const { level, parentID } = data;

    if (level === 0 && dataTree) {
      return dataTree;
    }

    if (!parentID) return [];

    const findCurrentLevel = dynamicLevels.find((item) => item.id === parentID);
    if (findCurrentLevel) {
      return findCurrentLevel.children || [];
    }
    //Children of first level
    return pageWithMetric?.children || [];
  };

  const handleChangeLevel = (data: IOnChangeFunnelParams) => {
    const { id: newPathId, parentID, selectedID: oldPathId, level, locationPath } = data;
    //Change first level
    if (level === 0) {
      setDynamicLevels([]);
      setFirstLevelLocationPath(locationPath);
      setCachedFunnelChartData(pathAnalysisCacheKey, {
        firstLocationPath: locationPath,
        dynamicLevels: [],
      });
      return;
    }

    const oldIndex = dynamicLevels.findIndex((level) => level.id === oldPathId);
    if (oldIndex === -1) return;

    //Remove all levels after oldId
    const updatedLevels = dynamicLevels.slice(0, oldIndex + 1);

    //Find new page based on newId and parentID
    const availableChildren = getPagesByParentID({ parentID });
    const selectedPage = availableChildren.find((page) => page.id === newPathId);
    if (selectedPage) {
      //Replace old page with new page
      updatedLevels[oldIndex] = selectedPage;
      setDynamicLevels(updatedLevels);
    }
  };

  const handleDeleteLevel = (id?: string) => {
    if (!id) return;

    const index = dynamicLevels.findIndex((level) => level.id === id);
    if (index === -1) return;

    const updatedLevels = dynamicLevels.slice(0, index);
    setDynamicLevels(updatedLevels);
  };

  const updateFirstLevel = () => {
    if (!listPageAnalyticFirstLevel.length || !firstLevelLocationPath) return;

    const matchingPage = listPageAnalyticFirstLevel.find(
      (page) => page.locationPath === firstLevelLocationPath,
    );

    if (matchingPage?.id) {
      handleSetFirstLevelPage(matchingPage.id);
      return;
    }
    if (listPageAnalyticFirstLevel[0]?.id) {
      handleSetFirstLevelPage(listPageAnalyticFirstLevel[0].id);
    }
  };

  useEffect(() => {
    updateFirstLevel();
  }, [listPageAnalyticFirstLevel, firstLevelLocationPath]);

  useEffect(() => {
    if (dataFirstLevels && dataFirstLevels.length > 0) {
      const cachedData = cachedFunnelChartData[pathAnalysisCacheKey];
      const firstLocationPath = cachedData?.firstLocationPath || dataFirstLevels[0].locationPath;

      setFirstLevelLocationPath(firstLocationPath);
    }
  }, [dataFirstLevels]);

  useEffect(() => {
    if (!pathAnalysisCacheKey) return;

    setCachedFunnelChartData(pathAnalysisCacheKey, {
      dynamicLevels: dynamicLevels,
    });
  }, [dynamicLevels]);

  useEffect(() => {
    return () => {
      setFirstLevelLocationPath(undefined);
      resetCachedFunnelChartData();
    };
  }, []);

  return {
    pageWithMetric,
    dynamicLevels,
    nextLevelPages,
    handleAddNextLevel,
    handleChangeLevel,
    getPagesByParentID,
    handleDeleteLevel,
  };
};
